import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, Wallet, Zap, BatteryCharging, Signal, Gauge, ShieldAlert,
  PowerOff, Power, RefreshCcw, Wifi, Pencil, Trash2, Cylinder, Repeat,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Area, AreaChart,
  BarChart, Bar, Legend,
} from "recharts";
import api, { formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import StatusPill from "@/components/StatusPill";
import CountryPhoneInput from "@/components/CountryPhoneInput";

// Format cellular RSSI for display.
// Meter uplinks may report EITHER raw CSQ index (0-31, per AT+CSQ / 3GPP TS 27.007)
// OR true dBm (negative). We normalise both.
//   • CSQ index → dBm ≈ -113 + 2×csq. 99 = no signal.
//   • Negative int → already dBm, pass through.
const formatRssi = (v) => {
  if (v === null || v === undefined) return { label: "—", sub: null };
  const n = Number(v);
  if (n === 99) return { label: "no signal", sub: null };
  if (n < 0) return { label: `${n} dBm`, sub: null };            // already dBm
  if (n >= 0 && n <= 31) {                                        // CSQ index
    const dbm = -113 + 2 * n;
    return { label: `CSQ ${n}`, sub: `~${dbm} dBm` };
  }
  return { label: `${n}`, sub: null };                            // unknown scale
};

const Metric = ({ icon: Icon, label, value, sub, tone = "text-slate-900", testId }) => (
  <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid={testId}>
    <div className="flex items-center gap-2 text-slate-500">
      <Icon size={14} />
      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold">{label}</div>
    </div>
    <div className={`font-heading text-2xl font-bold mt-2 tabular-nums ${tone}`}>{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-1 tabular-nums">{sub}</div>}
  </div>
);

// Format a byte count as B / KB / MB for human display.
const fmtBytes = (n) => {
  const b = Number(n || 0);
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(b < 10240 ? 2 : 1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
};

// Compact "Aug 12" tick for daily bars.
const shortDay = (iso) => {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch { return iso; }
};

const DataUsagePanel = ({ usage }) => {
  if (!usage) {
    return <div className="p-6 text-center text-sm text-slate-400" data-testid="data-usage-empty">Loading…</div>;
  }
  const t = usage.totals || {};
  const daily = usage.daily || [];
  const hasAny = daily.some((d) => d.uplink_bytes || d.downlink_bytes);
  return (
    <div className="p-5 space-y-5" data-testid="data-usage-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <UsageStat label="Uplink" value={fmtBytes(t.uplink_bytes)} sub={`${(t.uplink_frames || 0).toLocaleString()} frames`} tone="text-brand-700" testId="usage-uplink" />
        <UsageStat label="Downlink" value={fmtBytes(t.downlink_bytes)} sub={`${(t.downlink_frames || 0).toLocaleString()} frames`} tone="text-flame-600" testId="usage-downlink" />
        <UsageStat label="Total" value={fmtBytes(t.total_bytes)} sub={`over last ${usage.window_days} days`} testId="usage-total" />
        <UsageStat label="Avg / day" value={fmtBytes(t.avg_daily_bytes)} sub="both directions" testId="usage-avg" />
      </div>
      <div className="text-[11px] text-slate-500">
        Kampala calendar days · {usage.start_local} → {usage.end_local}. Bytes counted per transmitted frame (uplink = raw datagram received; downlink = command frame sent).
      </div>
      {hasAny ? (
        <div className="h-64 -mx-1" data-testid="data-usage-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={daily} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={shortDay}
                tick={{ fontSize: 10, fill: "#64748b" }}
                axisLine={{ stroke: "#cbd5e1" }}
                tickLine={false}
                interval="preserveStartEnd"
                minTickGap={16}
              />
              <YAxis
                tickFormatter={fmtBytes}
                tick={{ fontSize: 10, fill: "#64748b" }}
                axisLine={{ stroke: "#cbd5e1" }}
                tickLine={false}
                width={64}
              />
              <Tooltip
                cursor={{ fill: "rgba(148,163,184,0.08)" }}
                contentStyle={{ fontSize: 12, borderRadius: 2, border: "1px solid #e2e8f0" }}
                formatter={(v, name) => [fmtBytes(v), name]}
                labelFormatter={(l) => shortDay(l)}
              />
              <Legend
                wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
                iconType="square"
                iconSize={10}
              />
              <Bar dataKey="uplink_bytes" name="Uplink" stackId="a" fill="#2563eb" radius={[0, 0, 0, 0]} />
              <Bar dataKey="downlink_bytes" name="Downlink" stackId="a" fill="#ea580c" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="py-10 text-center text-sm text-slate-400 border border-dashed border-slate-200 rounded-sm">
          No radio traffic recorded in the last {usage.window_days} days.
        </div>
      )}
    </div>
  );
};

const UsageStat = ({ label, value, sub, tone = "text-slate-900", testId }) => (
  <div className="bg-slate-50 border border-slate-200 rounded-sm px-3 py-2" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{label}</div>
    <div className={`font-heading text-lg font-bold mt-0.5 tabular-nums ${tone}`}>{value}</div>
    {sub && <div className="text-[11px] text-slate-500 tabular-nums">{sub}</div>}
  </div>
);

export default function MeterDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [meter, setMeter] = useState(null);
  const [lastReading, setLastReading] = useState(null);
  const [readings, setReadings] = useState([]);
  const [commands, setCommands] = useState([]);
  const [recharges, setRecharges] = useState([]);
  const [valveHist, setValveHist] = useState([]);
  const [alarms, setAlarms] = useState([]);
  const [dailyBill, setDailyBill] = useState(null);
  const [monthlyBill, setMonthlyBill] = useState(null);
  const [cfg, setCfg] = useState(null);                    // /api/config — fleet price & currency

  // Dialogs
  const [valveTarget, setValveTarget] = useState(null); // "OPEN" | "CLOSED"
  const [rechargeOpen, setRechargeOpen] = useState(false);
  const [rechargeAmt, setRechargeAmt] = useState(200);
  const [rechargeReason, setRechargeReason] = useState("");
  const [processing, setProcessing] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ serial_number: "", imei: "", sim_number: "" });
  const [editErr, setEditErr] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [frames, setFrames] = useState([]);
  const [cylinder, setCylinder] = useState(null);          // /cylinder/level payload
  const [swapOpen, setSwapOpen] = useState(false);
  const [swapNote, setSwapNote] = useState("");
  const [swappingCyl, setSwappingCyl] = useState(false);
  const [dataUsage, setDataUsage] = useState(null);      // /data-usage payload (30-day)

  const load = useCallback(async () => {
    try {
      const [rt, hist, cmds, rch, vh, al, fr, cyl, cfgRes, du] = await Promise.all([
        api.get(`/meters/${id}/realtime`),
        api.get(`/meters/${id}/readings`, { params: { limit: 100 } }),
        api.get(`/meters/${id}/commands`),
        api.get(`/meters/${id}/recharge-orders`),
        api.get(`/meters/${id}/valve/history`),
        api.get(`/alarms`, { params: { meter_id: id, limit: 20 } }),
        api.get(`/meters/${id}/frames`, { params: { limit: 50 } }),
        api.get(`/meters/${id}/cylinder/level`),
        api.get(`/config`),
        api.get(`/meters/${id}/data-usage`, { params: { days: 30 } }),
      ]);
      setMeter(rt.data.meter);
      setLastReading(rt.data.last_reading);
      setReadings(hist.data.data || []);
      setCommands(cmds.data.data || []);
      setRecharges(rch.data.data || []);
      setValveHist(vh.data.data || []);
      setAlarms(al.data.data || []);
      setFrames(fr.data.data || []);
      setCylinder(cyl.data || null);
      setCfg(cfgRes.data?.data || null);
      setDataUsage(du.data || null);
      const [db, mb] = await Promise.all([
        api.get(`/meters/${id}/bills/daily`),
        api.get(`/meters/${id}/bills/monthly`),
      ]);
      setDailyBill(db.data); setMonthlyBill(mb.data);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  const confirmValve = async () => {
    if (!valveTarget) return;
    setProcessing(true);
    try {
      const { data } = await api.post(`/meters/${id}/valve`, { valve_state: valveTarget });
      toast.success(
        `Valve ${valveTarget === "OPEN" ? "open" : "close"} queued — will be sent on the meter's next uplink (cmd ${data.command.id.slice(0, 8)})`
      );
      setValveTarget(null);
      load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally { setProcessing(false); }
  };

  const submitRecharge = async (e) => {
    e.preventDefault();
    const amt = Number(rechargeAmt);
    if (!amt) {
      toast.error("Amount must be non-zero");
      return;
    }
    if (amt < 0 && !rechargeReason.trim()) {
      toast.error("Deductions require a reason");
      return;
    }
    setProcessing(true);
    try {
      const payload = { amount: amt };
      if (rechargeReason.trim()) payload.reason = rechargeReason.trim();
      await api.post(`/meters/${id}/recharge`, payload);
      toast.success(amt < 0 ? "Deduction queued" : "Recharge applied");
      setRechargeOpen(false);
      setRechargeReason("");
      load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally { setProcessing(false); }
  };

  const runQuery = async (cmd) => {
    try {
      const { data } = await api.post(`/meters/${id}/command`, { command_str: cmd });
      toast.success(`${cmd} queued (cmd ${data.id.slice(0, 8)})`);
      load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  };

  const submitSwap = async () => {
    setSwappingCyl(true);
    try {
      await api.post(`/meters/${id}/cylinder/swap`, { note: swapNote.trim() });
      toast.success("Cylinder swap recorded — level reset to 100 %");
      setSwapOpen(false);
      setSwapNote("");
      load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Swap failed");
    } finally { setSwappingCyl(false); }
  };

  const recomputeBalance = async () => {
    try {
      const { data } = await api.post(`/meters/${id}/recompute-balance`);
      toast.success(`Balance recomputed at cylinder-type price ${data.price_per_m3.toLocaleString()}/m³`);
      load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Recompute failed");
    }
  };

  if (!meter) {
    return <div className="text-sm text-slate-400" data-testid="meter-detail-loading">Loading meter…</div>;
  }

  const chartData = readings.map((r) => ({
    t: new Date(r.reported_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    reading: r.reading_m3,
    remaining: r.remaining_flow_m3,
  }));

  return (
    <div className="space-y-6" data-testid="meter-detail-page">
      <div className="flex items-start justify-between">
        <div>
          <Link to="/meters" className="text-xs text-slate-500 hover:text-blue-600 inline-flex items-center gap-1">
            <ArrowLeft size={12} /> Back to meters
          </Link>
          <div className="mt-2 flex items-center gap-3">
            <h1 className="font-heading text-2xl font-bold text-slate-900 font-mono tracking-tight" data-testid="meter-detail-id">
              {meter.deveui || meter.imei}
            </h1>
          </div>
          <div className="mt-1 text-sm text-slate-500">
            <span className="font-mono">{meter.serial_number}</span>
            {meter.sim_number && <> · SIM <span className="font-mono">+{meter.sim_number}</span></>}
            {meter._household && <> · Assigned to <span className="text-slate-700 font-medium">{meter._household.household_name}</span></>}
            {meter._area && <> · <span>{meter._area.name}</span></>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm"
            data-testid="meter-edit-button"
            onClick={() => {
              setEditForm({
                serial_number: meter.serial_number || "",
                imei: meter.imei || "",
                sim_number: meter.sim_number || "",
              });
              setEditErr(""); setEditOpen(true);
            }}
            className="rounded-sm"
          >
            <Pencil size={13} className="mr-1" /> Edit
          </Button>
          <Button
            variant="outline" size="sm"
            data-testid="meter-delete-button"
            onClick={async () => {
              if (!window.confirm(`Delete meter ${meter.serial_number}? This also removes all readings, recharges and alarms for this meter. This cannot be undone.`)) return;
              try {
                await api.delete(`/meters/${meter.id}`);
                toast.success("Meter deleted");
                nav("/meters");
              } catch (e) {
                toast.error(formatDetail(e.response?.data?.detail) || "Delete failed");
              }
            }}
            className="rounded-sm text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
          >
            <Trash2 size={13} className="mr-1" /> Delete
          </Button>
          <button onClick={load} className="text-sm text-slate-500 hover:text-blue-600 inline-flex items-center gap-1" data-testid="meter-refresh-button">
            <RefreshCcw size={14} /> Refresh
          </button>
        </div>
      </div>

      {/* Control bar */}
      <div className="bg-white border border-slate-200 rounded-sm p-4 flex flex-wrap items-center gap-3" data-testid="meter-control-bar">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mr-2">Actions</div>
        <Button
          data-testid="valve-control-open-button"
          onClick={() => setValveTarget("OPEN")}
          disabled={meter.valve_state === "OPEN"}
          className="bg-emerald-600 hover:bg-emerald-700 rounded-sm">
          <Power size={14} className="mr-1" /> Open valve
        </Button>
        <Button
          data-testid="valve-control-close-button"
          onClick={() => setValveTarget("CLOSED")}
          disabled={meter.valve_state === "CLOSED"}
          variant="destructive"
          className="rounded-sm">
          <PowerOff size={14} className="mr-1" /> Close valve
        </Button>
        <Button variant="outline" onClick={() => setRechargeOpen(true)} data-testid="meter-recharge-button" className="rounded-sm">
          <Wallet size={14} className="mr-1" /> Recharge
        </Button>
        <div className="w-px h-6 bg-slate-200" />
        <Button variant="outline" onClick={() => runQuery("queryFlowAndStatus")} data-testid="meter-query-flow-button" className="rounded-sm">
          <Gauge size={14} className="mr-1" /> Query flow & status
        </Button>
        <Button variant="outline" onClick={() => runQuery("queryBattery")} data-testid="meter-query-battery-button" className="rounded-sm">
          <BatteryCharging size={14} className="mr-1" /> Query battery
        </Button>
        <Button
          variant="outline"
          onClick={recomputeBalance}
          data-testid="meter-recompute-balance-button"
          title="Recompute balance = remaining_flow_m3 × cylinder-type price"
          className="rounded-sm"
        >
          <RefreshCcw size={14} className="mr-1" /> Recompute balance
        </Button>
        <div className="w-px h-6 bg-slate-200" />
        <Button
          variant="outline"
          onClick={() => setSwapOpen(true)}
          data-testid="cylinder-replaced-button"
          className="rounded-sm"
        >
          <Repeat size={14} className="mr-1" /> Cylinder replaced
        </Button>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {(() => {
          const currency = cfg?.currency || "UGX";
          const fleetPrice = Number(meter.price_per_m3 || 0);
          const balance = Number(meter.balance || 0);
          const remainingM3 = Number(meter.remaining_flow_m3 || 0);
          const readingM3 = Number(meter.reading_m3 || 0);
          // Fleet-price equivalents (what the platform believes the numbers are worth
          // at the currently-configured price — independent of the meter's own price field).
          const remainingValue = fleetPrice > 0 ? remainingM3 * fleetPrice : 0;
          const priceLabel = fleetPrice.toLocaleString(undefined, { maximumFractionDigits: 2 });
          return (
            <>
              <Metric
                icon={Zap}
                label="Cumulative"
                value={readingM3.toFixed(3)}
                sub="m³ · lifetime flow"
                testId="metric-reading"
              />
              <Metric
                icon={Gauge}
                label="Remaining"
                value={remainingM3.toFixed(3)}
                sub="m³"
                tone="text-blue-700"
                testId="metric-remaining"
              />
              <Metric
                icon={Wallet}
                label="Balance"
                value={`${currency} ${balance.toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
                testId="metric-balance"
              />
              <Metric
                icon={Power}
                label="Valve"
                value={meter.valve_state}
                tone={meter.valve_state === "OPEN" ? "text-emerald-700" : "text-slate-700"}
                testId="metric-valve"
              />
              <Metric
                icon={BatteryCharging}
                label="Battery"
                value={`${(meter.battery_voltage || 0).toFixed(2)}V`}
                tone={meter.battery_voltage < 3.6 ? "text-red-600" : "text-slate-900"}
                testId="metric-battery"
              />
              <Metric
                icon={Signal}
                label="Signal"
                value={formatRssi(meter.signal_rssi).label}
                sub={`${formatRssi(meter.signal_rssi).sub ? formatRssi(meter.signal_rssi).sub + " · " : ""}SNR ${meter.signal_snr} dB`}
                testId="metric-signal"
              />
            </>
          );
        })()}
      </div>

      {/* Cylinder level */}
      <CylinderLevelCard data={cylinder} onSwap={() => setSwapOpen(true)} />

      {/* Chart + last-reading */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm p-5" data-testid="meter-chart-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Consumption trend</div>
              <div className="font-heading text-lg font-semibold">Cumulative reading vs remaining credit</div>
            </div>
            <div className="text-xs text-slate-400 tabular-nums">{readings.length} points</div>
          </div>
          <div className="h-64">
            {readings.length === 0 ? (
              <div className="h-full grid place-items-center text-sm text-slate-400">
                No readings yet — trigger the Simulator to seed data.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="reading" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1E579E" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#1E579E" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="remaining" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#F79420" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#F79420" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="t" tick={{ fontSize: 10, fill: "#64748B" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#64748B" }} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 2, border: "1px solid #E2E8F0" }} />
                  <Area type="monotone" dataKey="reading" stroke="#1E579E" strokeWidth={2} fill="url(#reading)" name="Reading (m³)" />
                  <Area type="monotone" dataKey="remaining" stroke="#F79420" strokeWidth={2} fill="url(#remaining)" name="Remaining (m³)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="meter-lastreading-card">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Last uplink</div>
          <div className="font-heading text-lg font-semibold mb-3">Latest telemetry</div>
          {lastReading ? (
            <div className="space-y-2 text-sm">
              <Row k="Reported" v={new Date(lastReading.reported_at).toLocaleString()} mono />
              <Row k="Reading" v={`${lastReading.reading_m3.toFixed(3)} m³`} />
              <Row k="Δ Consumption" v={`${lastReading.delta_m3.toFixed(3)} m³`} />
              <Row k="Remaining" v={`${lastReading.remaining_flow_m3.toFixed(3)} m³`} />
              <Row k="Valve" v={lastReading.valve_state} />
              <Row k="Battery" v={`${lastReading.battery_voltage.toFixed(2)} V`} />
              <Row k="RSSI / SNR" v={`${formatRssi(lastReading.signal_rssi).label}${formatRssi(lastReading.signal_rssi).sub ? ` (${formatRssi(lastReading.signal_rssi).sub})` : ""} / ${lastReading.signal_snr} dB`} />
              <Row k="Source" v={lastReading.source} />
            </div>
          ) : (
            <div className="text-sm text-slate-400">Waiting for first uplink.</div>
          )}
        </div>
      </div>

      {/* Bills + history tabs */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="meter-bills-card">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Settlement</div>
          <div className="font-heading text-lg font-semibold mb-3">Bills</div>
          <div className="grid grid-cols-2 gap-3">
            <div className="border border-slate-200 rounded-sm p-3">
              <div className="text-[10px] uppercase text-slate-500 font-semibold">Today</div>
              <div className="font-heading text-2xl font-bold tabular-nums">UGX {(dailyBill?.amount ?? 0).toFixed(2)}</div>
              <div className="text-xs text-slate-500 tabular-nums">{(dailyBill?.consumption_m3 ?? 0).toFixed(3)} m³</div>
            </div>
            <div className="border border-slate-200 rounded-sm p-3">
              <div className="text-[10px] uppercase text-slate-500 font-semibold">This month</div>
              <div className="font-heading text-2xl font-bold tabular-nums">UGX {(monthlyBill?.amount ?? 0).toFixed(2)}</div>
              <div className="text-xs text-slate-500 tabular-nums">{(monthlyBill?.consumption_m3 ?? 0).toFixed(3)} m³</div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm" data-testid="meter-history-card">
          <Tabs defaultValue="frames" className="w-full">
            <TabsList className="w-full justify-start rounded-none border-b border-slate-200 bg-transparent px-3">
              <TabsTrigger value="frames" data-testid="tab-frames">Frames</TabsTrigger>
              <TabsTrigger value="alarms" data-testid="tab-alarms">Alarms</TabsTrigger>
              <TabsTrigger value="commands" data-testid="tab-commands">Commands</TabsTrigger>
              <TabsTrigger value="recharges" data-testid="tab-recharges">Recharges</TabsTrigger>
              <TabsTrigger value="valve" data-testid="tab-valve">Valve history</TabsTrigger>
              <TabsTrigger value="data-usage" data-testid="tab-data-usage">Data usage</TabsTrigger>
            </TabsList>
            <TabsContent value="frames" className="p-0" data-testid="frames-tab-content">
              {frames.length === 0 ? (
                <div className="p-6 text-center text-sm text-slate-400">No frames yet. Uplinks appear as soon as the meter reports; downlinks show up whenever a command is queued.</div>
              ) : (
                <div className="max-h-[420px] overflow-y-auto">
                  {frames.map((f, i) => {
                    const isUp = f.direction === "UPLINK";
                    return (
                      <div key={i} className="border-t border-slate-100 px-4 py-2 hover:bg-slate-50" data-testid={`frame-row-${i}`}>
                        <div className="flex items-center gap-3 mb-1">
                          <span className={`text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded-sm border ${isUp ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-brand-50 text-brand-700 border-brand-200"}`}>
                            {isUp ? "↑ Uplink" : "↓ Downlink"}
                          </span>
                          {f.cmd && <span className="text-xs font-mono font-semibold text-slate-700">{f.cmd}</span>}
                          {f.status && <StatusPill value={f.status} />}
                          <span className="ml-auto text-[11px] text-slate-500 font-mono">{f.ts ? new Date(f.ts).toLocaleString() : "—"}</span>
                        </div>
                        {f.hex && (
                          <div className="font-mono text-[11px] text-slate-700 break-all bg-slate-50 border border-slate-100 rounded-sm px-2 py-1">
                            {f.hex}
                          </div>
                        )}
                        <div className="mt-1 text-[10px] text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5">
                          {f.bytes ? <span>{f.bytes} bytes</span> : null}
                          {isUp && f.source ? <span>from {f.source}</span> : null}
                          {isUp && f.bridge_id ? <span>via {f.bridge_id}</span> : null}
                          {!isUp && f.fid !== undefined && f.fid !== null ? <span>fid={f.fid}</span> : null}
                          {!isUp && f.sent_at ? <span>sent {new Date(f.sent_at).toLocaleTimeString()}</span> : null}
                          {!isUp && f.ack_at ? <span>ack {new Date(f.ack_at).toLocaleTimeString()}</span> : null}
                          {!isUp && f.params && Object.keys(f.params).length > 0 ? (
                            <span className="font-mono">params={JSON.stringify(f.params)}</span>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>
            <TabsContent value="alarms" className="p-0">
              <SimpleList items={alarms} empty="No alarms." render={(a) => (
                <div className="flex items-center justify-between px-4 py-2 border-t border-slate-100">
                  <div>
                    <div className="text-sm font-medium text-slate-800">{a.code.replace(/_/g, " ")}</div>
                    <div className="text-xs text-slate-500 font-mono">{new Date(a.created_at).toLocaleString()}</div>
                  </div>
                  <StatusPill value={a.severity} />
                </div>
              )} />
            </TabsContent>
            <TabsContent value="commands" className="p-0">
              <SimpleList items={commands} empty="No commands sent." render={(c) => (
                <div className="flex items-center justify-between px-4 py-2 border-t border-slate-100">
                  <div>
                    <div className="text-sm font-medium text-slate-800 font-mono">{c.command_str}</div>
                    <div className="text-xs text-slate-500 font-mono">{new Date(c.created_at).toLocaleString()}</div>
                  </div>
                  <StatusPill value={c.status} />
                </div>
              )} />
            </TabsContent>
            <TabsContent value="recharges" className="p-0">
              <SimpleList items={recharges} empty="No recharges yet." render={(r) => (
                <div className="flex items-center justify-between px-4 py-2 border-t border-slate-100">
                  <div>
                    <div className="text-sm font-medium text-slate-800 font-mono">{r.order_no}</div>
                    <div className="text-xs text-slate-500 font-mono">{new Date(r.created_at).toLocaleString()}</div>
                  </div>
                  <div className="text-right">
                    <div className="tabular-nums">UGX {r.amount.toFixed(2)}</div>
                    <div className="text-xs text-slate-500 tabular-nums">{r.gas_amount_m3.toFixed(3)} m³</div>
                  </div>
                </div>
              )} />
            </TabsContent>
            <TabsContent value="valve" className="p-0">
              <SimpleList items={valveHist} empty="No valve events yet." render={(v) => (
                <div className="flex items-center justify-between px-4 py-2 border-t border-slate-100">
                  <div>
                    <div className="text-sm text-slate-800">{v.from_state} → <strong>{v.to_state}</strong></div>
                    <div className="text-xs text-slate-500 font-mono">{new Date(v.created_at).toLocaleString()} · {v.reason}</div>
                  </div>
                  <span className="text-xs text-slate-500 font-mono">{v.operator || "—"}</span>
                </div>
              )} />
            </TabsContent>
            <TabsContent value="data-usage" className="p-0" data-testid="data-usage-tab-content">
              <DataUsagePanel usage={dataUsage} />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Valve confirm dialog */}
      <Dialog open={!!valveTarget} onOpenChange={(o) => !o && setValveTarget(null)}>
        <DialogContent data-testid="valve-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <ShieldAlert className={valveTarget === "CLOSED" ? "text-red-500" : "text-emerald-500"} size={18} />
              Confirm valve {valveTarget === "OPEN" ? "OPEN" : "CLOSE"}
            </DialogTitle>
            <DialogDescription>
              You are about to <strong>{valveTarget === "OPEN" ? "open" : "close"}</strong> the gas valve on meter
              <span className="font-mono ml-1">{meter.deveui || meter.imei}</span>.
              {valveTarget === "CLOSED" && " This will interrupt gas supply to the household."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setValveTarget(null)} data-testid="valve-confirm-cancel">Cancel</Button>
            <Button
              data-testid="valve-confirm-modal-submit"
              disabled={processing}
              onClick={confirmValve}
              className={valveTarget === "OPEN" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}>
              {processing ? "Sending…" : `Confirm ${valveTarget === "OPEN" ? "OPEN" : "CLOSE"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recharge dialog — supports positive top-ups AND negative deductions */}
      <Dialog open={rechargeOpen} onOpenChange={setRechargeOpen}>
        <DialogContent data-testid="recharge-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Remote recharge / deduct</DialogTitle>
            <DialogDescription>
              Adjust the customer&apos;s prepaid gas credit. Positive value = top-up, negative value = deduction. Amount is converted to m³ at this household's cylinder-type price ({cfg?.currency || "UGX"} {Number(meter.price_per_m3 || 0).toLocaleString()}/m³ · edit under Configurations).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitRecharge} className="space-y-4">
            <div>
              <Label>Amount (UGX) — negative to deduct</Label>
              <Input type="number" step="0.01" value={rechargeAmt}
                onChange={(e) => setRechargeAmt(e.target.value)}
                data-testid="recharge-amount-input" className="mt-1" required />
              <div className="text-xs text-slate-500 mt-1 tabular-nums">
                ≈ {(Number(rechargeAmt || 0) / (Number(meter.price_per_m3) || 1)).toFixed(3)} m³
                {Number(rechargeAmt) < 0 && <span className="ml-2 text-orange-600 font-semibold">DEDUCTION</span>}
              </div>
            </div>
            {Number(rechargeAmt) < 0 && (
              <div>
                <Label>Reason (required for deductions)</Label>
                <Input type="text" value={rechargeReason || ""}
                  onChange={(e) => setRechargeReason(e.target.value)}
                  data-testid="recharge-reason-input" className="mt-1"
                  placeholder="e.g. reversal of duplicate payment, fraud clawback" />
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => setRechargeOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={processing || Number(rechargeAmt) === 0 || (Number(rechargeAmt) < 0 && !rechargeReason)}
                className={Number(rechargeAmt) < 0 ? "bg-orange-600 hover:bg-orange-700" : "bg-blue-600 hover:bg-blue-700"}
                data-testid="recharge-submit-button">
                {processing ? "Processing…" : (Number(rechargeAmt) < 0 ? "Apply deduction" : "Apply recharge")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit meter dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md rounded-sm" data-testid="meter-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit meter</DialogTitle>
            <DialogDescription>Update the meter&apos;s identifying details.</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setEditErr("");
              if (!editForm.serial_number.trim()) { setEditErr("Meter number is required"); return; }
              if (!editForm.imei.trim() || editForm.imei.trim().length < 14) { setEditErr("Valid IMEI required"); return; }
              setSavingEdit(true);
              try {
                const payload = {
                  serial_number: editForm.serial_number.trim(),
                  imei: editForm.imei.trim(),
                  sim_number: editForm.sim_number || null,
                };
                await api.put(`/meters/${meter.id}`, payload);
                toast.success("Meter updated");
                setEditOpen(false);
                load();
              } catch (err) {
                setEditErr(formatDetail(err.response?.data?.detail) || err.message);
              } finally { setSavingEdit(false); }
            }}
            className="space-y-4"
          >
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Meter number</Label>
              <Input
                required
                value={editForm.serial_number}
                onChange={(e) => setEditForm({ ...editForm, serial_number: e.target.value })}
                data-testid="meter-edit-serial-input"
                className="mt-1 rounded-sm font-mono"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">IMEI</Label>
              <Input
                required
                value={editForm.imei}
                onChange={(e) => setEditForm({ ...editForm, imei: e.target.value.replace(/\D/g, "").slice(0, 15) })}
                data-testid="meter-edit-imei-input"
                className="mt-1 rounded-sm font-mono tabular-nums"
                inputMode="numeric"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">SIM card number</Label>
              <div className="mt-1">
                <CountryPhoneInput
                  value={editForm.sim_number}
                  onChange={(v) => setEditForm({ ...editForm, sim_number: v })}
                  testId="meter-edit-sim"
                  placeholder="MSISDN on the SIM"
                />
              </div>
            </div>
            {editErr && (
              <div className="border border-red-200 bg-red-50 text-red-700 text-xs px-3 py-2 rounded-sm" data-testid="meter-edit-error">
                {editErr}
              </div>
            )}
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)} className="rounded-sm" data-testid="meter-edit-cancel">Cancel</Button>
              <Button type="submit" disabled={savingEdit} data-testid="meter-edit-submit"
                className="bg-brand-600 hover:bg-brand-700 rounded-sm">
                {savingEdit ? "Saving…" : "Save changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Cylinder swap confirm dialog */}
      <Dialog open={swapOpen} onOpenChange={setSwapOpen}>
        <DialogContent className="max-w-md rounded-sm" data-testid="cylinder-swap-dialog">
          <DialogHeader>
            <DialogTitle>Cylinder replaced</DialogTitle>
            <DialogDescription>
              This resets the cylinder level to 100 % and snapshots the meter&apos;s current
              cumulative reading as the new baseline. Use this after physically installing
              a fresh cylinder.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            {cylinder && (
              <div className="rounded-sm border border-slate-200 bg-slate-50 p-3 space-y-1 text-xs">
                <div className="flex justify-between"><span className="text-slate-500">Current baseline</span><span className="tabular-nums">{(cylinder.baseline_reading_m3 || 0).toFixed(3)} m³</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Current reading</span><span className="tabular-nums">{(cylinder.current_reading_m3 || 0).toFixed(3)} m³</span></div>
                <div className="flex justify-between"><span className="text-slate-500">New cylinder capacity</span><span className="tabular-nums">{cylinder.cylinder_kg} kg · {cylinder.cylinder_m3?.toFixed(3)} m³</span></div>
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Note (optional)</Label>
              <Input
                value={swapNote}
                onChange={(e) => setSwapNote(e.target.value)}
                data-testid="cylinder-swap-note-input"
                className="mt-1 rounded-sm"
                placeholder="e.g. tech ID, new cylinder serial…"
              />
            </div>
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => setSwapOpen(false)} className="rounded-sm" data-testid="cylinder-swap-cancel">Cancel</Button>
            <Button
              type="button"
              onClick={submitSwap}
              disabled={swappingCyl}
              data-testid="cylinder-swap-confirm"
              className="bg-flame-600 hover:bg-flame-700 rounded-sm"
            >
              {swappingCyl ? "Recording…" : "Confirm swap"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---- Cylinder Level card ------------------------------------------------
function CylinderLevelCard({ data, onSwap }) {
  if (!data) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm p-5 text-sm text-slate-400" data-testid="cylinder-level-card-loading">
        Loading cylinder level…
      </div>
    );
  }
  const pct = Math.max(0, Math.min(100, Number(data.remaining_pct || 0)));
  const isLow = data.is_low;
  const tone = isLow ? "bg-flame-500" : pct < 50 ? "bg-amber-500" : "bg-emerald-500";
  const bgTone = isLow ? "bg-flame-50 border-flame-200" : "bg-white border-slate-200";
  return (
    <div className={`border rounded-sm p-5 ${bgTone}`} data-testid="cylinder-level-card">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-sm grid place-items-center border ${isLow ? "bg-flame-100 border-flame-200 text-flame-700" : "bg-brand-50 border-brand-100 text-brand-700"}`}>
            <Cylinder size={18} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Cylinder level</div>
            <div className="font-heading text-lg font-semibold text-slate-900">
              {data.cylinder_kg} kg cylinder
              {isLow && <span className="ml-2 text-xs font-semibold text-flame-700 uppercase tracking-wider">· Low</span>}
            </div>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onSwap}
          data-testid="cylinder-level-swap-button"
          className="rounded-sm"
        >
          <Repeat size={13} className="mr-1" /> Cylinder replaced
        </Button>
      </div>

      <div className="mt-4">
        <div className="flex items-end justify-between mb-2">
          <div className="font-heading text-4xl font-bold tabular-nums text-slate-900" data-testid="cylinder-level-pct">
            {pct.toFixed(1)}%
          </div>
          <div className="text-xs text-slate-500 tabular-nums">
            {data.remaining_kg?.toFixed(2)} kg · {data.remaining_m3?.toFixed(3)} m³ remaining
          </div>
        </div>
        <div className="h-3 w-full rounded-sm bg-slate-100 overflow-hidden" data-testid="cylinder-level-bar">
          <div className={`h-full ${tone} transition-all`} style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-4 text-xs">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Consumed since swap</div>
          <div className="mt-1 tabular-nums text-slate-800 font-semibold">{data.consumed_kg?.toFixed(2)} kg</div>
          <div className="text-[10px] text-slate-400 tabular-nums">{data.consumed_m3?.toFixed(3)} m³</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Avg daily use (7-day)</div>
          <div className="mt-1 tabular-nums text-slate-800 font-semibold">{data.avg_daily_m3?.toFixed(3)} m³</div>
          <div className="text-[10px] text-slate-400 tabular-nums">≈ {((data.avg_daily_m3 || 0) / (data.lpg_m3_gas_per_kg || 1)).toFixed(3)} kg/day</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Days to empty</div>
          <div className="mt-1 tabular-nums text-slate-800 font-semibold" data-testid="cylinder-days-to-empty">
            {data.days_to_empty !== null && data.days_to_empty !== undefined ? `${data.days_to_empty} days` : "—"}
          </div>
          <div className="text-[10px] text-slate-400">
            {data.days_to_empty === null || data.days_to_empty === undefined ? "no consumption yet" : "at current rate"}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Last swap</div>
          <div className="mt-1 tabular-nums text-slate-800 font-semibold">
            {data.last_swap?.swapped_at ? new Date(data.last_swap.swapped_at).toLocaleDateString() : "—"}
          </div>
          <div className="text-[10px] text-slate-400">
            {data.last_swap?.operator_name || (data.last_swap ? "system" : "no swap on record")}
          </div>
        </div>
      </div>
    </div>
  );
}

const Row = ({ k, v, mono }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-slate-500">{k}</span>
    <span className={`text-slate-800 ${mono ? "font-mono text-xs" : "tabular-nums"}`}>{v}</span>
  </div>
);

const SimpleList = ({ items, empty, render }) => (
  <div>
    {(!items || items.length === 0) && <div className="p-6 text-center text-sm text-slate-400">{empty}</div>}
    {items && items.map((it) => <React.Fragment key={it.id}>{render(it)}</React.Fragment>)}
  </div>
);
